import { useEffect, useRef } from "react";
import { MEDIA_ERROR_EVENT } from "@/components/SmartImage";

/**
 * Keeps signed media links fresh on a page that shows job images.
 * Signed URLs are short-lived by design (private object store). A tab left open, restored, or focused after a while
 * would otherwise show broken tiles - so we refetch: when the tab becomes visible/focused again, on a slow interval,
 * and (debounced) when an image reports a final load failure.
 */
export function useFreshMedia(reload, { intervalMs = 10 * 60 * 1000, enabled = true } = {}) {
  const last = useRef(0);
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  useEffect(() => {
    if (!enabled) return undefined;
    const run = (minGapMs) => {
      const now = Date.now();
      if (now - last.current < minGapMs) return;
      last.current = now;
      Promise.resolve(reloadRef.current?.()).catch(() => {});
    };
    const onVisible = () => { if (document.visibilityState === "visible") run(30 * 1000); };
    const onFocus = () => run(30 * 1000);
    const onMediaError = () => run(15 * 1000);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    window.addEventListener(MEDIA_ERROR_EVENT, onMediaError);
    const t = setInterval(() => run(intervalMs - 1000), intervalMs);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener(MEDIA_ERROR_EVENT, onMediaError);
      clearInterval(t);
    };
  }, [enabled, intervalMs]);
}
